/**
 * Client-side bounds on a case-list page request.
 *
 * The server is the authority: `CaseListQueryMapper.ClampPageSize` coerces the requested
 * page size into `[MIN, MAX]` with `Math.Clamp` regardless of what the SPA sends. This is
 * that same clamp, on the same shared limits (constants/pageLimits.ts), applied before the
 * request leaves the browser so the SPA never asks for a page the server would only bound
 * anyway. PORT-PARITY: same rule, same effective quantity, both sides — the server keeps a
 * unit test on its clamp, and this shares the limits module rather than a copied number so
 * the two cannot drift.
 *
 * Coerce, do not reject: an out-of-range value becomes a valid page size, exactly as the
 * server treats it, rather than an error for a value the server is content to bound.
 */
import { PAGE_SIZE_LIMITS } from '../constants/pageLimits';

/**
 * Bounds a requested page size to the shared range, taking the default when none is given.
 *
 * @param requested The page size the caller asked for, if any.
 * @returns The default when none was asked for, or the request coerced into `[MIN, MAX]`.
 */
export function clampPageSize(requested: number | undefined): number {
  if (requested === undefined || !Number.isFinite(requested)) {
    return PAGE_SIZE_LIMITS.DEFAULT;
  }

  const whole = Math.trunc(requested);

  return Math.min(Math.max(whole, PAGE_SIZE_LIMITS.MIN), PAGE_SIZE_LIMITS.MAX);
}
